import React from 'react';

import { DefaultContext } from '@apollo/client';

import { Nullable } from '@clearverify/types';

import {
  QueryMode,
  useVerifierBillingBulkUpdateMutation,
  useVerifierBillingListQuery,
  useVerifierBillingUpdateMutation,
  VerifierBillingListDocument,
  VerifierBillingUpdate,
  VerifierBillingUpdateMany,
  VerifierBillingWhere,
} from 'src/api';
import { FilterKeys } from 'src/constants/filter';

import { useFilterStorage, useSearchQueryVariables } from '../common';

export const useVerifierBillingList = () => {
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilter);
  const { sortQueryVariable, getPaginationQueryVariablesByKey, filter } = useFilterStorage();

  const paginationQueryVariables = getPaginationQueryVariablesByKey(
    FilterKeys.VerifiersBillingTable,
  );

  const filterQueryVariables = React.useMemo(() => {
    const verifier = filter.verifier as Nullable<{ id: string }>;
    return verifier?.id
      ? {
          verifierId: { equals: verifier.id },
        }
      : null;
  }, [filter.verifier]);

  const { data, loading, previousData } = useVerifierBillingListQuery({
    variables: {
      filter: {
        ...searchQueryVariables,
        ...filterQueryVariables,
      },
      ...paginationQueryVariables,
      orderBy: sortQueryVariable,
    },
  });

  const [verifierBillingUpdateMutation, { loading: itemUpdating }] =
    useVerifierBillingUpdateMutation({
      refetchQueries: [VerifierBillingListDocument],
    });

  const [verifierBillingUpdateByFilterMutation, { loading: listUpdating }] =
    useVerifierBillingBulkUpdateMutation({
      refetchQueries: [VerifierBillingListDocument],
    });

  const updateVerifierBilling = React.useCallback(
    async (params: { id: string; data: VerifierBillingUpdate; context?: DefaultContext }) => {
      const { id, data, context } = params;
      try {
        await verifierBillingUpdateMutation({
          variables: {
            where: { id },
            data,
          },
          context,
        });
      } catch (error) {
        console.error({ error });
      }
    },
    [verifierBillingUpdateMutation],
  );

  const bulkUpdateVerifierBilling = React.useCallback(
    async (params: {
      data: VerifierBillingUpdateMany;
      filter: VerifierBillingWhere;
      context?: DefaultContext;
    }) => {
      const { data, filter, context } = params;
      try {
        await verifierBillingUpdateByFilterMutation({
          variables: {
            data,
            filter,
          },
          context,
        });
      } catch (error) {
        console.error(error);
      }
    },
    [verifierBillingUpdateByFilterMutation],
  );

  return {
    verifiersBillingsListCount: data?.verifierBillingCount || 0,
    verifiersBillings: loading
      ? (previousData?.verifierBillingList ?? [])
      : (data?.verifierBillingList ?? []),
    updateVerifierBilling,
    bulkUpdateVerifierBilling,
    loading,
    itemUpdating,
    listUpdating,
  };
};

const prepareSearchFilter = (searchValue: Nullable<string>): VerifierBillingWhere =>
  searchValue && searchValue.length > 0
    ? {
        verifier: {
          displayName: {
            contains: searchValue,
            mode: QueryMode.Insensitive,
          },
        },
      }
    : {};
